import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3, Users, QrCode, Store, Tag, Megaphone, Layout, Settings,
  LogOut, Menu, X, Briefcase, Wallet, Layers, PiggyBank, Sparkles,
  KeyRound, Bell, Link2,
} from 'lucide-react';
import Notificaciones from '../components/admin/Notificaciones';
import Statistics from '../components/admin/Statistics';
import Enlaces from '../components/admin/Enlaces';
import PantallaConFallo from '../components/admin/PantallaConFallo';
import SubscribersManagement from '../components/admin/SubscribersManagement';
import QrRedemptionsManagement from '../components/admin/QrRedemptionsManagement';
import StoresManagement from '../components/admin/StoresManagement';
import AccesosDeTienda from '../components/admin/AccesosDeTienda';
import BenefitsManagement from '../components/admin/BenefitsManagement';
import AnnouncementsManagement from '../components/admin/AnnouncementsManagement';
import LandingEditor from '../components/admin/LandingEditor';
import AdminSettingsPage from '../components/admin/AdminSettingsPage';
import SavingsEquivalences from '../components/admin/SavingsEquivalences';
import PriceShowcaseManagement from '../components/admin/PriceShowcaseManagement';
import AsistenteChat from '../components/admin/AsistenteChat';
import { VondiLogo } from '../components/ui/VondiLogo';
import ClosersManagement from '../components/admin/ClosersManagement';
import CloserMetrics from '../components/admin/CloserMetrics';
import CommissionTiersEditor from '../components/admin/CommissionTiersEditor';
import ComisionesPorPagar from '../components/admin/ComisionesPorPagar';
import { authService } from '../services/auth';
import { adminAvisosAPI } from '../utils/api';

const NAV_GROUPS = [
  {
    label: 'Analítica',
    items: [
      /* «Notificaciones», no «Avisos»: ese nombre ya lo ocupan los anuncios
         que se le mandan a los usuarios, y confundirlos costaría un susto. */
      { id: 'notificaciones', label: 'Notificaciones', icon: Bell },
      { id: 'asistente',     label: 'Asistente',  icon: Sparkles },
      { id: 'dashboard',        label: 'Overview',    icon: BarChart3  },
      { id: 'subscribers',      label: 'Miembros',    icon: Users      },
      { id: 'redemptions',      label: 'Canjes QR',   icon: QrCode     },
      /* Closers y canales juntos, con su QR, el interruptor del beneficio de
         bienvenida y su embudo. Sustituye a «Canales» y «Promotores». */
      { id: 'enlaces',          label: 'Enlaces',     icon: Link2 },
    ],
  },
  {
    label: 'Closers',
    items: [
      { id: 'closers',          label: 'Equipo',     icon: Briefcase },
      { id: 'closer-metrics',   label: 'Comisiones', icon: Wallet    },
      { id: 'commission-tiers', label: 'Escalones',  icon: Layers    },
    ],
  },
  {
    label: 'Contenido',
    items: [
      { id: 'stores',        label: 'Tiendas',    icon: Store     },
      /* Los accesos van aparte del editor de tiendas: es material para
         imprimir y repartir, no configuración que se edita tienda por tienda. */
      { id: 'accesos',       label: 'Accesos',    icon: KeyRound  },
      { id: 'benefits',      label: 'Beneficios', icon: Tag       },
      { id: 'announcements', label: 'Avisos',     icon: Megaphone },
      { id: 'editor',        label: 'Landing',    icon: Layout    },
      /* Muestrario de precios reales: es contenido de la app, no configuración —
         Iván sube fotos y precios igual que sube tiendas y beneficios. */
      { id: 'showcase',      label: 'Muestrario', icon: Tag       },
    ],
  },
  {
    label: 'Configuración',
    items: [
      { id: 'equivalences', label: 'Equivalencias', icon: PiggyBank },
      { id: 'settings',  label: 'Ajustes',    icon: Settings },
    ],
  },
];

const ALL_ITEMS = NAV_GROUPS.flatMap(g => g.items);

function NavItem({ item, active, onClick, insignia = 0 }) {
  const Icon = item.icon;
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 9,
        padding: '8px 10px', borderRadius: 8, width: '100%', border: 'none',
        background: active ? '#0A0A0A' : 'transparent',
        color: active ? '#fff' : '#374151',
        fontSize: 13, fontWeight: active ? 600 : 400,
        cursor: 'pointer', textAlign: 'left',
        transition: 'background .12s, color .12s',
      }}
    >
      <Icon size={14} color={active ? '#fff' : '#9CA3AF'} strokeWidth={active ? 2.2 : 1.8} />
      <span style={{ flex: 1 }}>{item.label}</span>
      {/* La insignia es lo que hace que alguien entre. Sin ella, una sección de
          notificaciones es una pestaña más que nadie abre. */}
      {insignia > 0 && (
        <span style={{
          minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9,
          background: '#EF4444', color: '#fff',
          fontSize: 10, fontWeight: 700, lineHeight: '18px', textAlign: 'center',
        }}>
          {insignia}
        </span>
      )}
    </button>
  );
}

function Sidebar({ activeTab, onTabChange, onLogout, pendientes = 0 }) {
  return (
    <aside style={{
      width: 220, background: '#fff',
      borderRight: '1px solid rgba(0,0,0,0.06)',
      display: 'flex', flexDirection: 'column',
      padding: '18px 12px', height: '100%', overflowY: 'auto', flexShrink: 0,
    }}>
      {/* Brand */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '4px 6px 20px' }}>
        {/* La esfera del manual (§3) en vez del monograma «V» provisional: el
            wordmark ya va al lado, así que aquí solo hace falta el átomo. */}
        <div style={{
          width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
          background: 'radial-gradient(circle at 33% 28%, #FFC9A3 0%, #FA4D0A 34%, #F52B16 100%)',
        }} />
        <div>
          <div style={{ marginBottom: 2 }}>
            <VondiLogo variant="negro" size={13} />
          </div>
          <div style={{ fontSize: 10, color: '#A1A1A6', letterSpacing: '0.03em', textTransform: 'uppercase' }}>
            Panel de administración
          </div>
        </div>
      </div>

      {/* Nav groups */}
      <nav style={{ display: 'flex', flexDirection: 'column', gap: 18, flex: 1 }}>
        {NAV_GROUPS.map((group, gi) => (
          <div key={gi}>
            <div style={{
              fontSize: 10, fontWeight: 600, color: '#9CA3AF',
              letterSpacing: '0.08em', textTransform: 'uppercase',
              padding: '0 10px', marginBottom: 4,
            }}>
              {group.label}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {group.items.map(item => (
                <NavItem
                  key={item.id}
                  item={item}
                  active={activeTab === item.id}
                  onClick={() => onTabChange(item.id)}
                  insignia={item.id === 'notificaciones' ? pendientes : 0}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* User footer */}
      <div style={{ paddingTop: 14, borderTop: '1px solid rgba(0,0,0,0.06)', marginTop: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '0 6px' }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%', background: '#0A0A0A', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, fontWeight: 700, flexShrink: 0,
          }}>A</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#0A0A0A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Administrador
            </div>
            <div style={{ fontSize: 10.5, color: '#A1A1A6', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              admin@miembros.mx
            </div>
          </div>
          <button
            onClick={onLogout}
            title="Cerrar sesión"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 6, flexShrink: 0 }}
          >
            <LogOut size={13} color="#A1A1A6" />
          </button>
        </div>
      </div>
    </aside>
  );
}

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  /*
    El conteo de la insignia.

    Se recarga al cambiar de sección, no en un temporizador: así, cuando el
    administrador arregla algo en Notificaciones y se va a otra parte, el número
    baja solo. Un contador que se queda pegado en «4» después de resolverlo todo
    enseña a ignorarlo.
  */
  const [pendientes, setPendientes] = useState(0);

  const revisarPendientes = useCallback(async () => {
    try {
      const r = await adminAvisosAPI.getAll();
      setPendientes(r?.data?.total ?? 0);
    } catch {
      /* Si no se puede comprobar, no se inventa un número: se deja sin insignia. */
      setPendientes(0);
    }
  }, []);

  useEffect(() => { revisarPendientes(); }, [revisarPendientes, activeTab]);

  const handleTabChange = (id) => {
    setActiveTab(id);
    setMobileOpen(false);
  };

  const activeItem = ALL_ITEMS.find(t => t.id === activeTab);

  const renderContent = () => {
    switch (activeTab) {
      case 'notificaciones':   return <Notificaciones />;
      case 'asistente':        return <AsistenteChat />;
      case 'dashboard':        return <Statistics />;
      case 'subscribers':      return <SubscribersManagement />;
      case 'redemptions':      return <QrRedemptionsManagement />;
      case 'stores':           return <StoresManagement />;
      case 'accesos':          return <AccesosDeTienda />;
      case 'benefits':         return <BenefitsManagement />;
      case 'announcements':    return <AnnouncementsManagement />;
      case 'editor':           return <LandingEditor />;
      case 'equivalences':     return <SavingsEquivalences />;
      case 'showcase':         return <PriceShowcaseManagement />;
      case 'settings':         return <AdminSettingsPage />;
      case 'enlaces':          return <Enlaces />;
      case 'closers':          return <ClosersManagement />;
      case 'closer-metrics':   return (
        <div className="p-8 space-y-6">
          <ComisionesPorPagar />
          <CloserMetrics />
        </div>
      );
      case 'commission-tiers': return <CommissionTiersEditor />;
      default:                 return <Statistics />;
    }
  };

  return (
    <div style={{
      height: '100vh', display: 'flex', background: '#F9FAFB',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      overflow: 'hidden',
    }}>
      {/* Desktop sidebar */}
      <div className="hidden lg:block" style={{ height: '100%' }}>
        <Sidebar activeTab={activeTab} onTabChange={handleTabChange} onLogout={handleLogout} pendientes={pendientes} />
      </div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="lg:hidden" style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex' }}>
          <div
            style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)' }}
            onClick={() => setMobileOpen(false)}
          />
          <div style={{ position: 'relative', zIndex: 1, height: '100%' }}>
            <Sidebar activeTab={activeTab} onTabChange={handleTabChange} onLogout={handleLogout} pendientes={pendientes} />
          </div>
        </div>
      )}

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        {/* Top bar */}
        <header style={{
          height: 56, padding: '0 24px',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          background: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              className="lg:hidden"
              onClick={() => setMobileOpen(!mobileOpen)}
              style={{
                width: 32, height: 32, borderRadius: 8, background: '#F5F5F7',
                border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              {mobileOpen ? <X size={15} /> : <Menu size={15} />}
            </button>
            <span style={{ fontSize: 15, fontWeight: 600, color: '#0A0A0A', letterSpacing: '-0.01em' }}>
              {activeItem?.label ?? 'Admin'}
            </span>
          </div>

          <button
            className="hidden lg:flex"
            onClick={handleLogout}
            style={{
              alignItems: 'center', gap: 6, height: 32, padding: '0 12px',
              borderRadius: 8, background: 'transparent',
              border: '1px solid rgba(0,0,0,0.08)',
              fontSize: 12, fontWeight: 500, cursor: 'pointer', color: '#6B7280',
            }}
          >
            <LogOut size={12} /> Salir
          </button>
        </header>

        {/* Scrollable content */}
        <main style={{ flex: 1, overflow: 'auto', background: '#F9FAFB' }}>
          <PantallaConFallo seccion={activeTab}>{renderContent()}</PantallaConFallo>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
